import {
  Card,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
  TableHead,
  TablePagination,
  Chip,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import { formatSierraLeoneLeone, getStatusColor } from "../utils/commonFuns";

interface ITableList {
  columns: Record<string, any>[];
  data: Record<string, any>[];
  actionsRows?: any | ((row: Record<string, any>, index: number) => React.ReactNode);
  searchQuery: string;
}

function TableListFrontend({
  columns,
  data = [],
  actionsRows,
  searchQuery,
}: ITableList) {
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const filteredData = useMemo(() => {
    const trimmedQuery = (searchQuery || "").trim();
    return data.filter((row) =>
      Object.values(row)
        .join(" ")
        .toLowerCase()
        .includes(trimmedQuery.toLowerCase())
    );
  }, [data, searchQuery]);

  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortColumn] ?? "";
      const bVal = b[sortColumn] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortDirection === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredData, sortColumn, sortDirection]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleSort = (columnId: string) => {
    if (columnId === "actions") return;
    if (sortColumn === columnId) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  className="text-sm font-medium bg-primary-lighter text-black"
                >
                  <TableSortLabel
                    active={sortColumn === column.id}
                    direction={sortColumn === column.id ? sortDirection : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              {actionsRows && (
                <TableCell className="text-sm font-medium bg-primary-lighter text-black">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={row.id || index}>
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    {column.id.toLowerCase().includes("date") ? (
                      dayjs(row[column.id]).format("MM/DD/YYYY hh:mm")
                    ) : column.id.toLowerCase().includes("amount") ? (
                      <span
                        className={`${
                          row[column.id] < 0 ? "text-error" : "text-success"
                        }`}
                      >
                        {formatSierraLeoneLeone(Number(row[column.id]) || 0)}
                      </span>
                    ) : column.id.toLowerCase().includes("balance") ? (
                      <div>
                        {formatSierraLeoneLeone(Number(row[column.id]) || 0)}
                      </div>
                    ) : column.id.toLowerCase().includes("link") &&
                      row[column.id] ? (
                      <></>
                    ) : column.id.toLowerCase().includes("status") ? (
                      <Chip
                        label={row[column.id] || "N/A"}
                        color={getStatusColor(row[column.id])}
                      />
                    ) : (
                      row[column.id] || "N/A"
                    )}
                  </TableCell>
                ))}
                {actionsRows && (
                  <TableCell>
                    {typeof actionsRows === 'function' 
                      ? actionsRows(row, index)
                      : actionsRows?.props?.children[index] ?? ""}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredData.length === 0 && (
        <div className="text-center p-4">No data found</div>
      )}

      {filteredData.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      )}
    </Card>
  );
}

export default TableListFrontend;
